import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { BookOpen, BookLock, DollarSign, Heart, LogOut, Moon, Sun, ChevronRight, Sparkles, Settings as SettingsIcon, Mail } from "lucide-react";
import { Wordmark } from "../components/BrandLogo";
import { api } from "../lib/api";
import { toast } from "sonner";
import T from "../components/T";

export default function Me() {
  const navigate = useNavigate();
  const { profile, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();

  const toggleTheme = async () => {
    const next = theme === "soft" ? "night" : "soft";
    setTheme(next);
    try { await api.patch(`/me/theme?theme=${next}`); } catch {}
  };

  const items = [
    { to: "/settings", icon: SettingsIcon, key: "me.settings_label", testid: "me-settings" },
    { to: "/incident-log", icon: BookLock, key: "me.incident_label", testid: "me-incident" },
    { to: "/before-you-go-back", icon: Heart, key: "me.bygb_label", testid: "me-bygb" },
    { to: "/reflections", icon: BookOpen, key: "me.reflections_label", testid: "me-reflections" },
    { to: "/financial", icon: DollarSign, key: "me.financial_label", testid: "me-financial" },
    { to: "/signal-letter", icon: Mail, key: "me.letter_label", testid: "me-letter" },
  ];

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-me">
        <Wordmark className="mb-6" />
        <p className="font-script text-2xl text-brand">{t("me.soft_hello")}</p>
        <h1 className="font-serif text-4xl leading-tight">
          {profile?.display_name || t("me.friend")}
        </h1>
        <p className="mt-2 text-sm text-foreground/60">
          {profile?.is_anonymous ? t("me.anon_label") : profile?.email}
        </p>
      </div>

      <div className="px-8 space-y-3">
        {/* Featured Abuse Library — prominent entry */}
        <button
          data-testid="me-library"
          onClick={() => navigate("/library")}
          className="w-full text-left rounded-3xl bg-brand/8 border border-brand/20 p-5 hover:border-brand/50 hover:bg-brand/12 transition-all"
        >
          <div className="flex items-start gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-full bg-brand/15 text-brand shrink-0">
              <BookOpen className="w-5 h-5" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-widest text-brand">{t("me.library_featured_kicker")}</p>
              <div className="mt-1 font-serif text-xl leading-snug">{t("me.library_featured_title")}</div>
              <p className="mt-1 text-sm text-foreground/70 leading-relaxed">{t("me.library_featured_sub")}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs text-brand">
                {t("me.library_featured_cta")} <ChevronRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </button>

        <button
          data-testid="me-theme-toggle"
          onClick={toggleTheme}
          className="w-full rounded-3xl bg-card border border-border p-5 flex items-center justify-between hover:border-brand/50 transition-all"
        >
          <div className="flex items-center gap-3">
            {theme === "soft" ? <Sun className="w-5 h-5 text-brand" /> : <Moon className="w-5 h-5 text-brand" />}
            <div>
              <div className="font-serif text-lg">{t("me.mode")}</div>
              <div className="text-sm text-foreground/60">{theme === "soft" ? t("me.mode_soft") : t("me.mode_night")}</div>
            </div>
          </div>
          <span className="text-xs uppercase tracking-widest text-brand">{t("common.switch")}</span>
        </button>

        {items.map(({ to, icon: Icon, key, testid }) => (
          <button
            key={to}
            data-testid={testid}
            onClick={() => navigate(to)}
            className="w-full rounded-3xl bg-card border border-border p-5 flex items-center justify-between hover:border-brand/50 transition-all"
          >
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 grid place-items-center rounded-full bg-brand/10 text-brand">
                <Icon className="w-4 h-4" />
              </span>
              <span className="font-medium">{t(key)}</span>
            </div>
            <ChevronRight className="w-4 h-4 text-foreground/40" />
          </button>
        ))}

        {profile?.is_anonymous && (
          <button
            data-testid="me-create-account"
            onClick={() => navigate("/auth")}
            className="w-full rounded-3xl bg-brand/8 border border-brand/30 p-5 flex items-center gap-3 text-left hover:bg-brand/12 transition-all"
          >
            <Sparkles className="w-5 h-5 text-brand" />
            <div>
              <div className="font-serif text-lg text-brand-deep">{t("me.save_space_h")}</div>
              <div className="text-sm text-foreground/70">{t("me.save_space_sub")}</div>
            </div>
          </button>
        )}
      </div>

      <section className="px-8 mt-8">
        <div className="rounded-3xl bg-card border border-border p-6">
          <p className="font-script text-2xl text-brand">{t("me.support_kicker")}</p>
          <h3 className="font-serif text-2xl mt-1 leading-snug">
            <T i18nKey="me.support_h" />
          </h3>
          <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{t("me.support_sub")}</p>
          <Button
            data-testid="me-donate-btn"
            onClick={() => navigate("/app/donate")}
            className="mt-4 w-full rounded-full bg-brand hover:bg-brand/90 text-white h-12"
          >
            {t("me.donate")}
          </Button>
        </div>
      </section>

      {!profile?.is_anonymous && (
        <div className="px-8 mt-8">
          <Button
            data-testid="me-signout-btn"
            variant="outline"
            onClick={async () => { await logout(); toast(t("me.signed_out")); navigate("/welcome"); }}
            className="w-full rounded-full h-12"
          >
            <LogOut className="w-4 h-4 mr-2" /> {t("me.sign_out")}
          </Button>
        </div>
      )}

      <div className="h-10" />
    </AppShell>
  );
}
